import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { Constructor, Nullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ArrayHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ArrayHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { RadObjectConstructor } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { IControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control";
import { Menu, MenuItem } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Menu";
import { GraphicsUnitEnum, IFontDefinition } from "../../../Server/Models";
import { GetDesignerInfoResponse, GetDesignerInfoResponseService, IGetDesignerInfoResponse } from "../../../Server/PrintReportDesignerDataService";
import { ReportElement } from "./Controls/ReportElement";
import { NAMESPACE } from "./_Namespace";

function createMenuPath(menu: Menu, path: string)
{
    const
        pathParts = path.split("//");

    let
        currentMenuItem: MenuItem = menu;

    pathParts
        .forEach(pathPart =>
        {
            let
                childMenuItem = ArrayHelper.firstOrDefault(currentMenuItem.controlsGet(), item => item.text.get() === pathPart) as Nullable<MenuItem>;

            if (childMenuItem === null)
            {
                childMenuItem = new MenuItem({ text: pathPart });
                currentMenuItem.append(childMenuItem);
            }

            currentMenuItem = childMenuItem;
        });

    return currentMenuItem;
}

interface IServerTypeInfo
{
    assembly: string;
    className?: string;
    namespace: string;
    typeString?: string;

    classConstructor: RadObjectConstructor<ReportElement>;
}

const
    serverTypes = new Map<RegExp | string, () => Promise<Constructor<ReportElement>>>();

export class PrintReportDesignerStatic
{
    // #region Static

    public static classNamespace = NAMESPACE + "PrintReportDesignerStatic";
    public static className = "PrintReportDesignerStatic";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Global Variables

    public static defaultFont: IFontDefinition = {
        bold: false,
        family: "Arial",
        graphicsUnit: GraphicsUnitEnum.point,
        italic: false,
        size: 10,
        sizeInPoints: 10,
        strikeout: false,
        underline: false
    };
    public static designerInfo: GetDesignerInfoResponse;
    public static fonts: string[] = [];
    public static reportElementMenu: Menu;
    public static serverTypes: IServerTypeInfo[] = [];

    // #endregion

    // #region Public Methods

    public static async createFromServerTypeAsync(typeId: string, propertyValues?: any & { templateProperties: any }): Promise<ReportElement>
    {
        for (let [key, value] of serverTypes)
        {
            if (!((typeof key === "string" && key === typeId) || (key instanceof RegExp && key.test(typeId))))
                continue;

            const
                classConstructor = await value();

            return new classConstructor(propertyValues);
        }

        throw new Error(`Type \"${typeId}\" not found`);
    }
    public static registerServerType(typeIdRegex: RegExp | string, createFn: () => Promise<Constructor<ReportElement>>): void
    {
        serverTypes.set(typeIdRegex, createFn);
    }
    public static registerServerTypes(types: { typeIdRegex: RegExp | string, createFn: () => Promise<Constructor<ReportElement>> }[])
    {
        types.forEach(item => this.registerServerType(item.typeIdRegex, item.createFn));
    }
    public static setControlFont(control: IControl, font: IFontDefinition, zoom: number)
    {
        if (!control)
            return;

        font = font ?? this.defaultFont;

        const
            $$element = control.$$element.get(),
            zoomedFontSize = font.size * zoom;

        Assert.isNotNullOrUndefined($$element, "$$element");

        $$element.css({
            "line-height": StringHelper.format("{0}px", zoomedFontSize * 1.2),
            "font-family": font.family || StringHelper.empty,
            "font-size": StringHelper.format("{0}px", zoomedFontSize),
            "font-style": font.italic ? "italic" : "normal",
            "font-weight": font.bold ? "bold" : "normal",
            "text-decoration": font.strikeout ? "line-through" : (font.underline ? "underline" : "none"),
        });
    }
    public static async setDesignerInfoAsync(designerInfo: IGetDesignerInfoResponse)
    {
        if (this.designerInfo)
            return;

        this.designerInfo = Object.assign(new GetDesignerInfoResponse(), designerInfo);

        ServiceManager.add(GetDesignerInfoResponseService, this.designerInfo);

        if (!this.reportElementMenu)
        {
            const
                designerInfo = ServiceManager.getRequired(GetDesignerInfoResponseService),
                menu = new Menu({ text: "New" });

            ArrayHelper
                .orderBy(
                    Object.values(designerInfo.reportElements),
                    reportElement => reportElement.menuPath
                )
                .forEach(reportElement =>
                {
                    const
                        menuItem = createMenuPath(menu, reportElement.menuPath);

                    menuItem.tag.set(reportElement);
                });

            PrintReportDesignerStatic.reportElementMenu = menu;

            //PrintReportDesignerStatic.reportElementMenu =
            //    new Menu({ text: "New" })
            //        .append(new MenuItem({ text: "Box", tag: { classConstructor: BoxElement } }))
            //        .append(new MenuItem({ text: "Field Element (Database)", tag: { classConstructor: FieldElementDatabase } }))
            //        .append(new MenuItem({ text: "Line (Horizontal)", tag: { classConstructor: LineElement, lineElementType: LineElementTypeEnum.horizontal } }))
            //        .append(new MenuItem({ text: "Line (Vertical)", tag: { classConstructor: LineElement, lineElementType: LineElementTypeEnum.vertical } }))
            //        .append(new MenuItem({ text: "Picture", tag: { classConstructor: PictureElement } }))
            //        .append(new MenuItem({ text: "Text", tag: { classConstructor: TextElement } }));
        }

        await this.setFontsAsync(designerInfo.fontFamilies);
    }
    public static setFontsAsync(fontFamilies: string[])
    {
        return (
            document.fonts.ready
                .then(() =>
                {
                    fontFamilies
                        .forEach(item =>
                        {
                            if (document.fonts.check(StringHelper.format("12px \"{0}\"", item)))
                                this.fonts.push(item);
                        });
                })
        );
    }

    // #endregion

    // #region Global Variables

    // #endregion

    // #region Public Methods

    // #endregion

    // #endregion
}